import {DrawerAccordion, DrawerAccordionItem} from "@/pages/extensions/components/DrawerAccordion.tsx";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form.tsx";
import {AdjustmentsVerticalIcon,} from "@heroicons/react/16/solid";
import {Input} from "@/components/ui/input.tsx";
import {useFieldArray, useForm, UseFormReturn} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {useExtensionEditDrawer} from "@/pages/extensions/components/ProviderExtensionEdit.tsx";
import {Extension} from "@/types/extenions.ts";
import {Switch} from "@/components/ui/switch.tsx";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
    SelectGroup,
    SelectLabel
} from "@/components/ui/select.tsx";
import {useGetExtensionsTypes} from "@/hooks/useExtensionsTypes.ts";
import collect, {Collection} from "collect.js";
import {ExtensionType} from "@/types/Systems.ts";
import {ReactNode, useEffect, useMemo, useState} from "react";
import {DescriptionSetting} from "@/components/DescriptionSetting.tsx";
import {PlusCircleIcon, TrashIcon} from "@heroicons/react/24/outline";
import {Button, LoadingButton} from "@/components/ui/button.tsx";
import {cn} from "@/lib/utils.ts";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from "@/components/ui/dialog.tsx";
import {Alert, AlertDescription} from "@/components/ui/alert.tsx";
import {ScrollArea} from "@/components/ui/scroll-area.tsx";
import {extensionsApi, useGetSchemaQuery, useUpdateExtensionMutation} from "@/store/extensions.ts";
import {JsonSchema} from "@/pages/extensions/components/JsonSchema.tsx";
import {JsonSchemaObject, jsonSchemaToZod} from "json-schema-to-zod";
import {AudioPlayerFromId} from "@/pages/extensions/components/AudioPlayerFromId.tsx";
import {RJSFSchema} from "@rjsf/utils";
import {store, useDispatch} from "@/store";
import {Tabs, TabsContent, TabsTrigger, TabsList} from "@/components/ui/tabs.tsx";
import {allowSubExtensions, requiredOpenMsg} from "@/pages/extensions/helpers.ts";
import {toast} from "sonner";


const formSchema = (schema: boolean | RJSFSchema | JsonSchemaObject | undefined) => {

    const zodCode = jsonSchemaToZod(schema, {
        module: 'none',
        parserOverride: (schema: RJSFSchema) => {
            if(schema.format === 'time') {
                return "z.string().regex(/^$|^([01]\\d|2[0-3]):([0-5]\\d)(?::([0-5]\\d))?$/, {message: 'שגיאה בפורמט השעה'})"
            }
            if(schema.format === 'date') {
                return "z.string().regex(/^$|^\\d{4}-\\d{2}-\\d{2}$/, {message: 'תאריך לא תקין'})"
            }
        }
    }); // מחזיר מחרוזת כמו 'z.object({ ... })'

    const buildSchema = new Function('z', `return ${zodCode}`);
    const dynamicSchema = buildSchema(z); // תוצאה: ZodSchema אמיתית

    return z.object({
        name: z.string().nullable(),
        cls_type: z.string(),
        order: z.number().nullable(),
        path: z.string().nullable(),
        settings: z.object({
            is_open: z.boolean().default(true),
            user_type: z.string().default('ST'),
            time_active: z.array(z.object({
                from: z.string(),
                to: z.string(),
            })).default([]),
            required_login: z.string().default('no_ident'),
            class_user_student_only: z.boolean().default(false),
            other_class: z.string().default(""),
            class_user_teacher_only: z.boolean().default(false),
        }).merge(schema ? dynamicSchema : z.object({})).catchall(z.unknown()),
        // teacher_phone: z.array(z.string()).refine(items => new Set(items).size === items.length, {
        //     message: 'מספרי טלפון לא יכולים לחזור על עצמם',
        // }),
        teacher_phone: z.array(z.object({
            number: z.string()
        })).superRefine((phones, ctx) => {
            const seen = new Set<string>();
            phones.forEach((item, index) => {
                if (item.number.trim() === '') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'מספר טלפון לא יכול להיות ריק',
                        path: [index, 'number'],
                    })
                } else if (seen.has(item.number)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'מספר טלפון כפול',
                        path: [index, 'number'],
                    });
                } else {
                    seen.add(item.number);
                }
            });
        }),
        open_msg_id: z.number().optional().nullable(),
        sclass_audio_name_id: z.number().optional().nullable(),
        sf_name_id: z.number().optional().nullable(),
        pin_code_extension: z.string().nullish(),
        pin_code_admin_only: z.string().nullish(),
        var_bool: z.boolean(),
    });
};

const defaultSettings = {
    is_open: true,
    user_type: 'ST',
    time_active: [],
    required_login: 'no_ident',
    class_user_student_only: false,
    class_user_teacher_only: false,
    other_class: "",
}

export function EditDrawerForm() {
    const {isOpen, close, extension: _extension} = useExtensionEditDrawer()
    const extension = _extension as Extension;

    const [updateExtension, {isLoading, error}] = useUpdateExtensionMutation();
    const dispatch = useDispatch();
    const [clsType, setClsType] = useState(extension.cls_type);

    const {data: schema} = useGetSchemaQuery(clsType);

    function getInitialExtensionData() {
        return {
            ...(extension as unknown as z.infer<typeof formSchema>),
            settings: {
                ...defaultSettings,
                ...extension.settings,
            },
            teacher_phone: extension.teacher_phone ?
                extension.teacher_phone.trim().split(',').map(p => ({number: p.trim()})) : []
        }
    }

    const schemaWithZodResolver = useMemo(() => zodResolver(formSchema(schema)), [schema]);

    const form = useForm<z.infer<typeof formSchema>>({
        defaultValues: getInitialExtensionData(),
        resolver: schemaWithZodResolver,
    });
    const showOtherClass = form.watch("settings.class_user_student_only");
    const currentClsType = form.watch('cls_type');

    useEffect(() => setClsType(currentClsType), [currentClsType]);

    const extensionsTypes = useGetExtensionsTypes(t => t.priority < 100);

    async function handleSubmit(data: z.infer<typeof formSchema>) {

        const extensionData = {
            ...extension,
            ...data,
            teacher_phone: data.teacher_phone.map(p => p.number.trim()).join(','),
        }

        return await updateExtension(extensionData as unknown as Extension).unwrap()
            .then(extension => {
                dispatch(extensionsApi.util.updateQueryData('getExtension', extension.id, (oldData) =>
                    ({
                        ...oldData,
                        ...extension,
                    } as Extension)
                ));
                toast.success('השלוחה עודכנה בהצלחה');
            })
            .catch(err => {
                toast.error('שגיאה בעדכון השלוחה', {
                    description: err.data.msg,
                })
                throw err;
            })
    }

    return (
        <Form {...form}>
            <ScrollArea dir="rtl" className="text-gray-800 flex-grow min-h-0 overflow-auto">
                <DrawerAccordion>
                    <DrawerAccordionItem value="general" title="הגדרות כלליות" icon={AdjustmentsVerticalIcon}>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="settings.is_open"
                                render={({field}) => (
                                    <FormItem className="space-y-2 col-span-2">
                                        <FormControl>
                                            <FormLabel
                                                className="justify-end text-xs leading-[10.4px] items-center flex gap-4">
                                                <span className="text-gray-600">שלוחה פעילה</span>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormLabel>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />


                            <FormField
                                control={form.control}
                                name="name"
                                render={({field}) => (
                                    <FormItem className="space-y-2">
                                        <FormLabel className="text-xs leading-[10.4px]">שם שלוחה</FormLabel>
                                        <FormControl>
                                            <Input className="h-9" placeholder="שם השלוחה" {...field} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="path"
                                render={({field}) => (
                                    <FormItem className="space-y-2">
                                        <FormLabel className="text-xs leading-[10.4px]">נתיב שלוחה</FormLabel>
                                        <FormControl>
                                            <Input className="h-9" {...field} disabled={!extension.conf_id.includes('.')} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="cls_type"
                                render={({field}) => (
                                    <FormItem className="space-y-2">
                                        <FormLabel className="text-xs leading-[10.4px]">סוג שלוחה</FormLabel>
                                        <Select
                                            dir="rtl"
                                            onValueChange={field.onChange}
                                            defaultValue={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="בחר סוג שלוחה"/>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="max-h-96">
                                                {collect(extensionsTypes)
                                                    .groupBy<Collection<ExtensionType>, string>('group')
                                                    .map((types, group) =>
                                                        <SelectGroup key={group}>
                                                            <SelectLabel>{group}</SelectLabel>
                                                            {types.map(type =>
                                                                <SelectItem key={type.id} value={type.value}>
                                                                    {type.name}
                                                                </SelectItem>
                                                            ).all()}
                                                        </SelectGroup>
                                                    ).values<ReactNode>().all()}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </DrawerAccordionItem>
                    <DrawerAccordionItem
                        value="extension_records_system"
                        title="הקלטות תפעול שלוחה"
                        icon={AdjustmentsVerticalIcon}
                    >
                        <Tabs dir="rtl" defaultValue="open_msg">
                            <TabsList className="border-b bg-transparent border-gray-200 border-x-0 border-t-0 px-0 py-0 flex justify-between items-center gap-2 rounded-none">
                                <TabsTrigger value="open_msg" className="font-normal text-xs rounded-none data-[state=active]:!shadow-none data-[state=active]:!bg-transparent border-x-0 border-t-0 data-[state=active]:border-b-2 data-[state=active]:border-light-blue-700 data-[state=active]:text-light-blue-700">פתיח שלוחה</TabsTrigger>
                                <TabsTrigger value="sclass_audio_name" className="font-normal text-xs rounded-none data-[state=active]:!shadow-none data-[state=active]:!bg-transparent border-x-0 border-t-0 data-[state=active]:border-b-2 data-[state=active]:border-light-blue-700 data-[state=active]:text-light-blue-700">שם שלוחה</TabsTrigger>
                                {allowSubExtensions.includes(extension.cls_type) &&
                                        <TabsTrigger value="sf_name"
                                              className="font-normal text-xs rounded-none data-[state=active]:!shadow-none data-[state=active]:!bg-transparent border-x-0 border-t-0 data-[state=active]:border-b-2 data-[state=active]:border-light-blue-700 data-[state=active]:text-light-blue-700">פתיח
                                    זמני</TabsTrigger>}
                            </TabsList>
                            <TabsContent value="open_msg">
                                <FormField
                                    control={form.control}
                                    name="open_msg_id"
                                    render={({field}) => (
                                        <AudioPlayerFromId
                                            id={field.value}
                                            onChange={id =>  field.onChange(id)}
                                            onDelete={requiredOpenMsg.includes(extension.cls_type) ? () => field.onChange(null) : undefined}
                                        />
                                    )}/>
                            </TabsContent>
                            <TabsContent value="sclass_audio_name">
                                <FormField
                                    control={form.control}
                                    name="sclass_audio_name_id"
                                    render={({field}) => (
                                        <AudioPlayerFromId
                                            onDelete={() => field.onChange('')}
                                            id={field.value}
                                            onChange={id =>  field.onChange(id)}
                                        />
                                    )}/>
                            </TabsContent>
                            <TabsContent value="sf_name">
                                <FormField
                                    control={form.control}
                                    name="sf_name_id"
                                    render={({field}) => (
                                        <AudioPlayerFromId
                                            onDelete={() => field.onChange('')}
                                            id={field.value}
                                            onChange={id =>  field.onChange(id)}
                                        />
                                    )}/>
                            </TabsContent>
                        </Tabs>

                    </DrawerAccordionItem>
                    <DrawerAccordionItem value="securty" title="הרשאות וסיסמאות" icon={AdjustmentsVerticalIcon}>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                            <FormField
                                control={form.control}
                                name="pin_code_extension"
                                render={({field}) => (
                                    <FormItem className="space-y-2">
                                        <FormLabel className="flex gap-1 items-center text-xs leading-[10.4px]">
                                            <span>סיסמת כניסה</span>
                                            <DescriptionSetting>
                                                סיסמת כניסה כללית, מיועד בעיקר לנעילת שלוחה לכלל המשתמשים, מלבד המנהל.
                                            </DescriptionSetting>
                                        </FormLabel>
                                        <FormControl>
                                            <Input className="h-9" placeholder="ללא סיסמא" {...field} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="pin_code_admin_only"
                                render={({field}) => (
                                    <FormItem className="space-y-2">
                                        <FormLabel className="flex gap-1 items-center text-xs leading-[10.4px]">
                                            <span>סיסמת כניסה למורה</span>
                                            <DescriptionSetting>
                                                <p>
                                                    סיסמא זו תאפשר למשתמש בכיר (בדרך כלל מורה או מנהל קבוצה) כדי לגשת
                                                    לשלוחה בהרשאה גבוהה, ניתן לאפשר את הגישה הגבוהה עבור כל מספר טלפון
                                                    שברשימת הבכירים להלן .
                                                </p>
                                                <p>
                                                    משתמש מתוך רשימה זו יוכל להקיש <span
                                                    className="font-semibold">0</span> ע"מ להיכנס כמשתמש רגיל (מאזין).
                                                </p>

                                            </DescriptionSetting>
                                        </FormLabel>
                                        <FormControl>
                                            <Input className="h-9" placeholder="ללא סיסמא" {...field} />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="settings.user_type"
                                render={({field}) => (
                                    <FormItem className="space-y-2 col-span-2">
                                        <FormLabel className="text-xs leading-[10.4px]">מורשה כניסה לשלוחה</FormLabel>
                                        <Select
                                            dir="rtl"
                                            onValueChange={field.onChange}
                                            defaultValue={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="כולם"/>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="max-h-96">
                                                <SelectItem value="TE">מלמד</SelectItem>
                                                <SelectItem value="AD">מנהל</SelectItem>
                                                <SelectItem value="ST">תלמיד</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />
                            {/*
                            <select class="form-control" name="root[required_login]" id="root[required_login]"><option value="no_ident">לא נדרשת הזדהות</option><option value="once_phone">הזדהות חד פעמית - לפי טלפון שחייג</option><option value="once_tz">הזדהות חד פעמית - לפי סיסמה/ת.ז.</option><option value="always_phone">הזדהות בכל כניסה לשלוחה - לפי טלפון שחייג</option><option value="always_tz">הזדהות בכל כניסה לשלוחה - לפי סיסמה/ת.ז.</option><option value="insert_phone_if_not_regular">משתמש שאינו מסוג רשום - נדרש להקיש טלפון של משתמש רשום</option><option value="press_tz_if_not_regular">אם הטלפון של המשתמש אינו רשום במערכת, נדרש להזדהות באמצעות ת.ז.</option><option value="insert_phone">הזדהות ע"י הקשת מספר טלפון</option><option value="insert_phone_play_name">הזדהות בהקשת מספר טלפון + הקראת שם המשתמש (המרת טקסט לדיבור)</option><option value="insert_phone_and_create_user">הזדהות בהקשת מספר טלפון + יצירת משתמש זמני חדש אם אין</option><option value="insert_tz">נדרש להקיש ת.ז. פעם אחת, אם לא מופיע בתלמיד</option><option value="connect_or_create_user_by_tz">הקשת ת.ז. תקינה לשם הזדהות או יצירת משתמש חדש</option><option value="teacher_by_phone_and_password">לפי 'מספרי טלפון של מורה' + סיסמת מורה (אם הוגדרה)</option></select>
                            */}
                            <FormField
                                control={form.control}
                                name="settings.required_login"
                                render={({field}) => (
                                    <FormItem className="space-y-2 col-span-2">
                                        <FormLabel className="text-xs leading-[10.4px]">הזדהות בכניסה לשלוחה</FormLabel>
                                        <Select
                                            dir="rtl"
                                            onValueChange={field.onChange}
                                            defaultValue={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="לא נדרשת הזדהות"/>
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="max-h-96">
                                                <SelectItem value="no_ident">לא נדרשת הזדהות</SelectItem>
                                                <SelectItem value="once_phone">הזדהות חד פעמית - לפי טלפון
                                                    שחייג</SelectItem>
                                                <SelectItem value="once_tz">הזדהות חד פעמית - לפי
                                                    סיסמה/ת.ז.</SelectItem>
                                                <SelectItem value="always_phone">הזדהות בכל כניסה לשלוחה - לפי טלפון
                                                    שחייג</SelectItem>
                                                <SelectItem value="always_tz">הזדהות בכל כניסה לשלוחה - לפי
                                                    סיסמה/ת.ז.</SelectItem>
                                                <SelectItem value="auth_id_mosad_and_callerid">נדרש להקיש קוד אישי/ת.ז.
                                                    המופיע במשתמש עם הטלפון שחייג</SelectItem>
                                                <SelectItem value="insert_phone_if_not_regular">משתמש שאינו מסוג רשום -
                                                    נדרש להקיש טלפון של משתמש רשום</SelectItem>
                                                <SelectItem value="press_tz_if_not_regular">אם הטלפון של המשתמש אינו
                                                    רשום במערכת, נדרש להזדהות באמצעות ת.ז.</SelectItem>
                                                <SelectItem value="insert_phone">הזדהות ע"י הקשת מספר טלפון</SelectItem>
                                                <SelectItem value="insert_phone_play_name">הזדהות בהקשת מספר טלפון +
                                                    הקראת שם המשתמש (המרת טקסט לדיבור)</SelectItem>
                                                <SelectItem value="insert_phone_and_create_user">הזדהות בהקשת מספר טלפון
                                                    + יצירת משתמש זמני חדש אם אין</SelectItem>
                                                <SelectItem value="insert_tz">נדרש להקיש ת.ז. פעם אחת, אם לא מופיע
                                                    בתלמיד</SelectItem>
                                                <SelectItem value="connect_or_create_user_by_tz">הקשת ת.ז. תקינה לשם
                                                    הזדהות או יצירת משתמש חדש</SelectItem>
                                                <SelectItem value="teacher_by_phone_and_password">לפי 'מספרי טלפון של
                                                    מורה' + סיסמת מורה (אם הוגדרה)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            <ActivityTime
                                {...form}
                            />

                            <FormField
                                control={form.control}
                                name="settings.class_user_student_only"
                                render={({field}) => (
                                    <div className="col-span-2 flex flex-col gap-1">
                                        <FormItem className="flex items-center gap-2 ">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <FormLabel className="flex gap-1 items-center text-xs leading-[10.4px]">
                                                <span>אפשרות התחברות למשתמשים לפי מספרי טלפון המופיעים במערכת בלבד</span>
                                            </FormLabel>
                                            <FormMessage/>
                                        </FormItem>

                                        <div className="ms-6">
                                            <Button asChild
                                                    className="cursor-pointer rounded-full max-h-7 px-6 bg-light-blue-50 text-light-blue-900 shadow-none"
                                                    variant="outline" size="sm">
                                                <a href={`/learning/sclassuser/?_sclass=${extension.id}`}>
                                                    צפיה ברשימת המשתמשים הרשומים לשלוחה זו
                                                </a>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            />
                            {showOtherClass && (
                                <FormField
                                    control={form.control}
                                    name="settings.other_class"
                                    render={({ field }) => (
                                        <div className="col-span-2 flex flex-col gap-1">
                                            <div className="ms-6 flex flex-col gap-2">
                                                <FormItem>
                                                    <FormLabel className="text-xs leading-[10.4px]">
                                                        לאפשר התחברות גם לתלמידים שרשומים לשלוחות:
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            className="h-9"
                                                            placeholder="הזן נתיבי שלוחות מופרדים בפסיקים"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            </div>
                                        </div>
                                    )}
                                />
                            )}




                            <FormField
                                control={form.control}
                                name="settings.class_user_teacher_only"
                                render={({field}) => (
                                    <div className="col-span-2 flex flex-col gap-1">
                                        <FormItem className="flex items-center gap-2 ">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <FormLabel className="flex gap-1 items-center text-xs leading-[10.4px]">
                                                <span>מורים יכולים להתחבר רק אם הטלפון שלהם רשום ברשימת טלפונים של מורים</span>
                                                <DescriptionSetting>
                                                    אם הגדרה זו מסומנת, מורה שינסה להיכנס לשלוחה זו ומספר הטלפון שלו
                                                    אינו
                                                    רשום בתיבת "טלפונים של מורה" שלעיל, המורה יוגדר בשלוחה זו ובכל
                                                    השלוחות
                                                    שמתחתיה - כתלמיד
                                                </DescriptionSetting>
                                            </FormLabel>
                                            <FormMessage/>
                                        </FormItem>
                                        <div className="ms-6">
                                            <TeacherPhones
                                                extensionName={extension.name ?? 'ללא שם'}
                                                {...form}
                                            />
                                        </div>
                                    </div>
                                )}
                            />
                        </div>
                    </DrawerAccordionItem>

                    {schema && (
                        <DrawerAccordionItem value="advenced" title="הגדרות מתקדמות" icon={AdjustmentsVerticalIcon}>
                            <FormField
                                control={form.control}
                                name="settings"
                                render={({field}) => (
                                    <FormItem>
                                        <FormControl>
                                            <JsonSchema
                                                schema={schema}
                                                value={field.value}
                                                onChange={field.onChange}
                                                onErrorsChange={e => console.log(e)}
                                            />
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}
                            />

                            {import.meta.env.DEV && (
                                <div className="w-[420px]">
                                    <div dir="ltr"
                                         className="max-w-full whitespace-pre-wrap bg-gray-100 p-4 rounded-md text-xs">
                                        {JSON.stringify(schema, null, 2)}
                                    </div>
                                </div>
                            )}
                        </DrawerAccordionItem>
                    )}


                </DrawerAccordion>
            </ScrollArea>

            <footer className="px-4 py-4 border-t border-gray-200 flex flex-row-reverse gap-2 items-center">
                <LoadingButton
                    isLoading={isLoading}
                    onClick={form.handleSubmit(handleSubmit)}
                >
                    <span className="text-xs">שמירה</span>
                </LoadingButton>

                <Button variant="outline" onClick={close}>
                    <span className="text-xs">ביטול</span>
                </Button>
            </footer>
        </Form>
    )
}

function TeacherPhones(props: UseFormReturn<z.infer<typeof formSchema>> & { extensionName?: string }) {
    const {control, extensionName} = props;
    const [open, setOpen] = useState(false);

    const {append, remove, fields} = useFieldArray({
        control,
        name: 'teacher_phone'
    });

    const oldValue = useMemo(() => props.getValues('teacher_phone'), [open])

    function cancel() {
        //reset the current value to old value
        props.setValue('teacher_phone', oldValue);
        setOpen(false);
    }

    async function closeWindow(event: Event) {
        event && event.preventDefault();

        const validate = await props.trigger('teacher_phone');

        if (validate) {
            setOpen(false);
            return;
        }
    }

    return (
        <Dialog
            open={open}
            onOpenChange={setOpen}
        >
            <DialogTrigger asChild>
                <Button type="button"
                        className="cursor-pointer rounded-full max-h-7 px-6 bg-light-blue-50 text-light-blue-900 shadow-none"
                        variant="outline" size="sm">
                    צפיה ברשימת הטלפונים המוגדרים כמורים ({oldValue.length})
                </Button>
            </DialogTrigger>
            <DialogContent
                showCloseButton={false}
                onInteractOutside={e => e.preventDefault()}
                onEscapeKeyDown={e => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>רשימת מספרי מורים לשלוחת {extensionName}</DialogTitle>
                </DialogHeader>
                <div className="px-6">
                    <FormField
                        control={control}
                        name="teacher_phone"
                        render={({field: parentField}) => (
                            <FormItem className="col-span-2 space-y-2">
                                <FormLabel className="text-xs leading-[10.4px]">מספרי מורים</FormLabel>
                                {fields.map((fieldSet, index) => (
                                    <div key={fieldSet.id} className="flex items-center gap-2">
                                        <FormField
                                            control={control}
                                            name={`teacher_phone.${index}.number`}
                                            render={({field}) => (
                                                <FormItem className="w-full">
                                                    <FormControl>
                                                        <Input {...field} className="h-9" placeholder="הקלד מספר"/>
                                                    </FormControl>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />

                                        <div className="flex items-center gap-1">
                                            <Button type="button" size="icon-sm" variant="ghost"
                                                    className="rounded-full text-gray-400"
                                                    onClick={() => remove(index)}
                                            >
                                                <TrashIcon/>
                                            </Button>

                                            <Button type="button"
                                                    onClick={() => append({
                                                        number: ''
                                                    })}
                                                    size="icon-sm"
                                                    variant="ghost"
                                                    className={cn('rounded-full', index !== parentField.value.length - 1 && 'opacity-0 pointer-events-none')}
                                            >
                                                <PlusCircleIcon className="!size-6"/>
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                {fields.length === 0 && (
                                    <Button
                                        className="w-full"
                                        variant="outline"
                                        onClick={() => append({
                                            number: ''
                                        })}
                                    >
                                        הוסף מספר
                                    </Button>
                                )}

                                <FormMessage/>
                            </FormItem>
                        )}
                    />
                </div>

                <div className="px-6">
                    <Alert className="bg-gray-50">
                        <AlertDescription>
                            שים לב: ההגדרה לא תישמר עד שתבצע שמירה בטופס הראשי
                        </AlertDescription>
                    </Alert>
                </div>

                <DialogFooter className="sm:flex-row-reverse sm:justify-start gap-2">
                    <Button
                        onClick={closeWindow}
                        type="button"
                    >
                        סגור
                    </Button>

                    <Button onClick={cancel} variant="outline" type="button">
                        ביטול
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function ActivityTime(props: UseFormReturn<z.infer<typeof formSchema>>) {

    const {control} = props;

    //array field with from time and to time
    const {append, move, remove, fields} = useFieldArray({
        control,
        name: 'settings.time_active'
    });

    return (
        <FormField
            control={control}
            name="settings.time_active"
            render={({field: parentField}) => (
                <FormItem className="col-span-2 space-y-2">
                    <FormLabel className="text-xs leading-[10.4px]">שעות פעילות</FormLabel>
                    {fields.map((fieldSet, index) => (
                        <div key={fieldSet.id} className="flex items-center gap-2">
                            <FormField
                                control={control}
                                name={`settings.time_active.${index}.from`}
                                render={({field}) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input   {...field} className="h-9" type="time" placeholder="החל מ"/>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={control}
                                name={`settings.time_active.${index}.to`}
                                render={({field}) => (
                                    <FormItem className="w-full">
                                        <FormControl>
                                            <Input className="h-9" type="time" placeholder="עד" {...field}/>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <div className="flex items-center gap-1">
                                <Button type="button" size="icon-sm" variant="ghost"
                                        className="rounded-full text-gray-400"
                                        onClick={() => remove(index)}
                                >
                                    <TrashIcon/>
                                </Button>

                                <Button type="button"
                                        onClick={() => append({
                                            from: '', to: ''
                                        })}
                                        size="icon-sm"
                                        variant="ghost"
                                        className={cn('rounded-full', index !== parentField.value.length - 1 && 'opacity-0 pointer-events-none')}
                                >
                                    <PlusCircleIcon className="!size-6"/>
                                </Button>
                            </div>
                        </div>
                    ))}

                    {fields.length === 0 && (
                        <Button
                            className="w-full"
                            variant="outline"
                            onClick={() => append({from: '', to: ''})}
                        >
                            הוסף שעות פעילות
                        </Button>
                    )}
                </FormItem>
            )}
        />
    )

}

const t = z.object({
    "make_stt": z.boolean().describe("לאפשרות זו יש עלות ביחידות").default(false),
    "play_beep": z.boolean().default(true),
    "record_to": z.string().describe("ניתן להגדיר שלוחה או מספר שלוחות שאליהם ההקלטה תיכנס, יש לכתוב את נתיב השלוחה. \nשלוחות מרובות יש להפריד בפסיקים").default(""),
    "order_msgs": z.enum(["-", ""]).default("-"),
    "play_title": z.boolean().default(false),
    "student_rec": z.boolean().default(true),
    "teacher_rec": z.boolean().default(true),
    "zintuk_dest": z.enum(["extension", "other_extensions", "school"]).describe("בהגדרת הרשומים לשלוחה, כברירת מחדל הצינתוק יישלח רק למי שנרשם לשלוחה הנוכחית, ניתן להלן לשנות שהצינתוק יישלח לרשומים בשלוחות אחרות").default("extension"),
    "enable_timer": z.boolean().describe("לאחר שהזמן שהוזן יחלוף המשתמשים לא יוכלו לשמוע את ההודעה בתיבה").default(false),
    "max_long_msg": z.number().int().describe("-1 ללא הגבלת אורך").default(-1),
    "save_msg_stt": z.boolean().describe("לאפשרות זו יש עלות ביחידות").default(false),
    "student_play": z.boolean().default(true),
    "teacher_play": z.boolean().default(true),
    "zintuk_tasks": z.array(z.object({
        "dom": z.string().describe("לדוגמה: 1-10,15,20-22 או *").default("1-31"),
        "dow": z.string().describe("לדוגמה: 1,2,4-6 או *").default("1-7"),
        "time": z.string().time().default("00:00"),
        "active": z.boolean().default(true),
        "months": z.string().describe("לדוגמה: 1-3,12 או *").default("*"),
        "caller_id": z.string().describe("כמובן ניתן להשתמש רק בזיהוי שמאופשר למוסד").default("")
    })).default([]),
    "control_digits": z.array(z.object({
        "key": z.enum(["skipms", "skipms2"]).default("0"),
        "seconds": z.number().int().default(30)
    })).max(2).describe("כברירת מחדל, מקשים 1-3 משמשים לדילוג 30 שניות קדימה/אחורה, ו4-6 דילוג של 300 שניות (5 דקות) קדימה/אחורה").default([]),
    "more_public_to": z.string().describe("ניתן להגדיר שלוחה או מספר שלוחות נוספות (בנוסף לשלוחה הנוכחית!) שאליהם ההקלטה תיכנס בעת פרסומה, יש לכתוב את נתיב השלוחה. \nשלוחות מרובות יש להפריד בפסיקים").default(""),
    "save_title_stt": z.boolean().describe("טקסט זה יישמר בשם ההקלטה, לאפשרות זו יש עלות ביחידות").default(false),
    "search_timeout": z.number().int().describe("-1 ללא הגבלת אורך").default(4),
    "choose_by_title": z.boolean().default(false),
    "play_total_msgs": z.boolean().default(true),
    "save_last_point": z.boolean().describe("ניתן להוריד את הסימון מתיבה משלוחות שבהם לא מעוניינים שהמערכת \"תזכור\" את המיקום האחרון").default(true),
    "student_promote": z.boolean().default(false),
    "teacher_promote": z.boolean().default(true),
    "zintok_callerid": z.string().describe("ברירת מחדל יוצא שיחה עם המספר הראשי של המערכת, ניתן לבחור את אחד מהמספרים היוצאים של המוסד").default(""),
    "min_msg_duration": z.number().int().describe("0 ללא הגבלת מינימום").default(1),
    "options_by_title": z.enum(["filter_without", "create_title"]).default("filter_without"),
    "send_msg_to_mail": z.string().describe("כאשר משתמש מקליט הודעה, ההודעה תישלח למייל שהוגדר. \nמיילים מרובים יש להפריד בפסיקים").default(""),
    "play_duration_min": z.number().int().describe("-1 ללא השמעת אורך, 0 משמיע תמיד אורך").default(3),
    "search_max_digits": z.number().int().default(10),
    "zintuk_to_teacher": z.enum(["yes", "no", "ask", "yes_always"]).describe("בבחירת 'כן, גם בהודעה שלא אושרה' ההודעה תהפוך להודעה מתלמיד למורה").default("yes"),
    "order_private_msgs": z.enum(["ask", "-id", "id"]).default("ask"),
    "sclass_to_zintokim": z.string().describe("כאשר מורה שולח צינתוקים לתלמידים, ניתן להגדיר מאיזה שלוחה או מספר שלוחות יילקח רשימת הרשומים לקבלת צינתוקים, יש לכתוב את נתיב השלוחה. \nשלוחות מרובות יש להפריד בפסיקים").default(""),
    "student_can_delete": z.boolean().default(false),
    "teacher_can_delete": z.boolean().default(true),
    "if_not_found_search": z.enum(["play_last", "play_not_found", "ask"]).default("play_last"),
    "student_rec_promote": z.boolean().default(false),
    "student_send_zintuk": z.boolean().default(false),
    "teacher_rec_promote": z.boolean().default(true),
    "teacher_send_zintuk": z.boolean().default(true),
    "custom_escape_digits": z.string().describe("אם הוגדרו אפשרויות הקשה (0-9) המערכת תשמור את הנתון שהוקש ב\"יומן שמיעת הקלטות למשתמשים\" לא יהיה ניתן לעבור להודעה הבאה לפני ההקשה. על מנת לאשר את ההקשה לפני השמירה, יש להוסיף לתיבה סימן קריאה (!) לדוגמה: 123!").default(""),
    "more_public_to_lines": z.array(z.object({
        "code": z.string().optional(),
        "path": z.string().optional(),
        "phone": z.string().optional()
    })).describe("ניתן להגדיר קווים נוספים להעתקת ההקלטות, יש להזין טלפון של מוסד, קוד הקלטות ונתיב שלוחה להעתקה").default([]),
    "search_by_numeration": z.boolean().describe("האם להשמיע בכניסה לשלוחה \"הקישו את מספר הקובץ הרצוי ובסיום סולמית, לשמיעת כל הקבצים הקישו 0 וסולמית\"").default(false),
    "student_after_hangup": z.enum(["temp", "delete", "msg_to_teacher"]).default("temp"),
    "teacher_record_title": z.enum(["no_rec", "before_record", "after_record"]).default("temp"),
    "play_count_wait_public": z.boolean().default(true),
    "play_from_other_sclass": z.string().describe("ניתן להגדיר מספר שלוחות שמהם יושמעו ההודעות, יש לכתוב את נתיבי השלוחה. \nשלוחות מרובות יש להפריד בפסיקים, * עבור כלל השלוחות, ~כדי להחריג שלוחות מסויימות").default(""),
    "play_you_received_call": z.boolean().describe("משמיע \"קיבלתם צינתוק עבור {שם מוקלט של המשתמש, אם קיים} למעבר לשלוחה שצינתקה הקישו 1\", \nבמידה ומבטלים אפשרות זו, חובה לתת ללקוח אפשרות קלה להסיר את עצמו מקבלת צינתוקים בכניסה למערכת!!!").default(true),
    "success_move_to_sclass": z.string().describe("נתיב שלוחה / שם שלוחה / ניתוק").default(""),
    "play_manual_before_msgs": z.boolean().default(true),
    "student_here_created_by": z.boolean().default(false),
    "student_play_last_point": z.boolean().default(false),
    "teacher_play_last_point": z.boolean().default(false),
    "student_registrer_zintuk": z.boolean().default(true),
    "teacher_registrer_zintuk": z.boolean().default(true),
    "student_delete_my_msg_only": z.boolean().describe("במידה והאפשרות מופעלת, תלמיד יכול למחוק הודעה שלו אחרי שהיא פורסמה").default(false),
    "teacher_delete_my_msg_only": z.boolean().describe("במידה והאפשרות מופעלת, מורה יכול למחוק הודעה שלו אחרי שהיא פורסמה").default(false),
    "play_title_from_other_sclass": z.boolean().describe("כותרת השלוחה תושמע במקרה שהוגדרו שלוחות מרובות מהם יושמעו ההודעות").default(false)
});
